/**
 * The destination carried in the query string, as this page sees it.
 *
 * Read, passed back to the API, and never trusted here. Validation against the
 * allowlist happens server-side in apps/api/src/lib/redirect.ts, because a
 * check performed in the browser is a check an attacker can skip. Two rules
 * follow from that and both are load bearing:
 *
 *   1. The value is never rendered. A URL from a query string is
 *      attacker-controlled text, and "you are being taken to <thing>" is a
 *      sentence somebody would write for you if you let them.
 *   2. The page never navigates to it directly. After sign-in it uses the
 *      `redirectTo` the API returned, which has been validated. When somebody
 *      is already signed in it hands over to /api/auth/authorize, which
 *      validates and redirects server-side.
 */

const PARAM = "redirect_uri";

export function readRedirectUri(search: string = window.location.search): string | null {
  const value = new URLSearchParams(search).get(PARAM);

  // Bounded because it is put back into a URL below. Nothing legitimate here is
  // anywhere near this long.
  return value && value.length <= 2048 ? value : null;
}

/**
 * Where to send somebody who is already signed in.
 *
 * /api/auth/authorize rather than the destination itself: it is the one place
 * that decides whether a destination is allowed, and routing through it means
 * this page has no opinion to get wrong. It also renews the session on the way
 * through, so a visitor who left the tab open overnight arrives with a fresh
 * access token instead of one that expires on the next click.
 */
export function continueUrl(redirectUri: string | null): string {
  const url = new URL("/api/auth/authorize", window.location.origin);
  if (redirectUri) url.searchParams.set(PARAM, redirectUri);
  return url.toString();
}
